import { useState, useEffect } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";

// Detects small screens so the combobox opens as a full-screen search modal on phones.
export function useIsMobile() {
  const [mobile, setMobile] = useState(() => typeof window !== "undefined" && window.matchMedia("(max-width: 640px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 640px)");
    const handler = (e) => setMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return mobile;
}

/**
 * Project-standard searchable dropdown (Combobox). Use this for ANY dropdown that can
 * hold more than ~10 options (materials, products, suppliers, customers, users, …).
 * Desktop: anchored popover with search. Mobile (≤640px): full-screen search modal.
 *
 * options: [{ value: string, label: string, keywords?: string }]
 */
export function SearchableSelect({
  value,
  onValueChange,
  options = [],
  placeholder = "Select…",
  searchPlaceholder = "Search…",
  emptyText = "No results found.",
  disabled = false,
  className,
  triggerClassName,
  testid,
  optionTestid,
}) {
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobile();
  const selected = options.find((o) => o.value === value);

  const trigger = (onClick) => (
    <Button
      type="button"
      variant="outline"
      role="combobox"
      aria-expanded={open}
      disabled={disabled}
      onClick={onClick}
      className={cn("w-full justify-between font-normal", !selected && "text-muted-foreground", triggerClassName)}
      data-testid={testid}
    >
      <span className="truncate">{selected ? selected.label : placeholder}</span>
      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
    </Button>
  );

  const list = (
    <Command>
      <CommandInput placeholder={searchPlaceholder} data-testid={testid ? `${testid}-search` : undefined} />
      <CommandList>
        <CommandEmpty>{emptyText}</CommandEmpty>
        <CommandGroup>
          {options.map((o) => (
            <CommandItem
              key={o.value}
              value={`${o.label} ${o.keywords || ""} ${o.value}`}
              onSelect={() => { onValueChange(o.value); setOpen(false); }}
              data-testid={testid ? (optionTestid ? optionTestid(o) : `${testid}-opt-${o.value}`) : undefined}
            >
              <Check className={cn("mr-2 h-4 w-4", value === o.value ? "opacity-100" : "opacity-0")} />
              <span className="truncate">{o.label}</span>
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </Command>
  );

  if (isMobile) {
    return (
      <div className={className}>
        {trigger(() => !disabled && setOpen(true))}
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent
            className="p-0 gap-0 max-w-full w-screen h-[100dvh] rounded-none border-0 top-0 left-0 translate-x-0 translate-y-0"
            data-testid={testid ? `${testid}-modal` : undefined}
          >
            {list}
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div className={className}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>{trigger(undefined)}</PopoverTrigger>
        <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)] min-w-[240px]" align="start">
          {list}
        </PopoverContent>
      </Popover>
    </div>
  );
}
